// services/BranchApiService.js
import {
    APIBrancheList,
    APIBrancheStore,
    APIBrancheUpdate,
    APIBrancheDelete,
    APIBrancheNames,
} from '../services/APIs';
import { api } from './authService';
import {
    connectionErrorMsg,
    Message_400,
    Message_401,
    Message_403,
    Message_404,
    Message_405,
    Message_409,
    Message_422,
    Message_500,
    Message_503
} from './errorMsgs';

class BranchApiService {
    constructor() {
        this.showToast = null;
    }

    /**
     * Set the toast function from useToast hook
     */
    setToastFunction(toastFunction) {
        this.showToast = toastFunction;
    }

    /**
     * Handle API errors consistently based on backend controller structure
     */
    handleError(error, defaultMessage = 'Operation failed') {
        let message = defaultMessage;
        let statusCode = null;
        let errors = null;
        let isIntegrityConstraint = false;

        if (error.response) {
            statusCode = error.response.status;
            const responseData = error.response.data || {};
            
            // Use backend message if available, otherwise fallback to our defaults
            message = responseData.message || defaultMessage;
            errors = responseData.errors || null;

            // Check for database integrity constraint errors
            const backendError = responseData.error || '';
            isIntegrityConstraint = backendError.includes('Integrity constraint violation') || 
                                   backendError.includes('foreign key constraint fails') ||
                                   backendError.includes('1451');

            // Handle specific backend response patterns
            switch (statusCode) {
                case 400:
                    // Handle business logic errors (like "Cannot delete branch with sub-branches")
                    if (responseData.message && responseData.message.includes('Cannot delete')) {
                        message = responseData.message;
                    } else {
                        message = message || Message_400;
                    }
                    break;
                case 401:
                    message = message || Message_401;
                    break;
                case 403:
                    message = message || Message_403;
                    break;
                case 404:
                    message = message || Message_404;
                    break;
                case 405:
                    message = message || Message_405;
                    break;
                case 409:
                    message = message || Message_409;
                    break;
                case 422:
                    // Backend returns validation errors with 'errors' object
                    if (errors) {
                        // Format validation errors into a readable string
                        const errorMessages = Object.values(errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        message = `${message || Message_422}:\n${errorMessages}`;
                    } else {
                        message = message || Message_422;
                    }
                    break;
                case 500:
                    // Handle database integrity constraint errors
                    if (isIntegrityConstraint) {
                        message = 'Cannot delete this branch because it has related data (departments, employees, etc.) assigned. ' +
                                 'Please remove or reassign the related data first before deleting this branch.';
                    } 
                    // Handle other database errors
                    else if (backendError && backendError.includes('Database Error')) {
                        message = `Database Error: ${message}`;
                    } else {
                        message = message || Message_500;
                    }
                    break;
                case 503:
                    message = message || Message_503;
                    break;
                default:
                    message = message || `Server error (${statusCode})`;
            }
        } else if (error.request) {
            message = connectionErrorMsg;
        } else {
            message = error.message || defaultMessage;
        }

        if (this.showToast) {
            this.showToast("Error", message, "error");
        } else {
            console.warn('Toast function not set. Message:', message);
        }

        return {
            success: false,
            message,
            statusCode,
            errors,
            isIntegrityConstraint,
            error: error.response?.data || error
        };
    }

    /**
     * Get branch names (for dropdowns)
     */
    async listNames(branchId = null) {
        try {
            const params = {};
            if (branchId) {
                params.branch_id = branchId;
            }
            const response = await api.get(APIBrancheNames, { params });
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    message: responseData.message || 'Branches fetched successfully'
                };
            } else {
                // Backend returned error status
                return {
                    success: false,
                    data: responseData.data || null,
                    message: responseData.message || 'Failed to fetch branches'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch branches');
        }
    }

    /**
     * Get branch list with full details (for table)
     */
    async list(branchId = null) {
        try {
            const params = {};
            if (branchId) {
                params.branch_id = branchId;
            }
            const response = await api.get(APIBrancheList, { params });
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                const branches = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    success: true,
                    data: branches,
                    message: responseData.message || 'Branches fetched successfully'
                };
            } else {
                // Backend returned error status
                return {
                    success: false,
                    data: responseData.data || null,
                    message: responseData.message || 'Failed to fetch branches'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch branches');
        }
    }

    /**
     * Create branch
     */
    async create(branchData) {
        try {
            const response = await api.post(APIBrancheStore, branchData);
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true, 
                    data: responseData.data,
                    message: responseData.message || 'Branch created successfully'
                };
            } else {
                // Backend returned error status (validation or duplicate)
                if (response.status === 422 && responseData.errors) {
                    // Format validation errors for display
                    const errorMessages = Object.values(responseData.errors)
                        .flat()
                        .map(err => `• ${err}`)
                        .join('\n');
                    return {
                        success: false,
                        data: responseData.data,
                        message: `${responseData.message || 'Validation failed'}:\n${errorMessages}`,
                        errors: responseData.errors
                    };
                }
                return {
                    success: false,
                    data: responseData.data,
                    message: responseData.message || 'Failed to create branch'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to create branch');
        }
    }

    /**
     * Update branch
     */
    async update(branchData) {
        try {
            const response = await api.post(APIBrancheUpdate, branchData);
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Branch updated successfully'
                };
            } else {
                // Handle different error cases
                if (response.status === 404) {
                    return {
                        success: false,
                        data: responseData.data,
                        message: responseData.message || 'Branch not found'
                    };
                } else if (response.status === 422) {
                    if (responseData.errors) {
                        // Format validation errors for display
                        const errorMessages = Object.values(responseData.errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        return {
                            success: false,
                            data: responseData.data,
                            message: `${responseData.message || 'Validation failed'}:\n${errorMessages}`,
                            errors: responseData.errors
                        };
                    } else if (responseData.message === 'Given Record Data Already Exists') {
                        return {
                            success: false,
                            data: responseData.data,
                            message: responseData.message,
                            errors: responseData.errors
                        };
                    }
                }
                return {
                    success: false,
                    data: responseData.data,
                    message: responseData.message || 'Failed to update branch'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to update branch');
        }
    }

    /**
     * Delete branch
     */
    async delete(formData) {
        try {
            const response = await api.post(APIBrancheDelete, formData);
            const responseData = response.data;
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Branch deleted successfully'
                };
            } else {
                // Handle business logic errors (400 status with custom message)
                if (response.status === 400 && responseData.message) {
                    return {
                        success: false,
                        data: responseData.data,
                        message: responseData.message,
                        statusCode: 400
                    };
                }
                if (response.status === 404) {
                    return {
                        success: false,
                        data: responseData.data,
                        message: responseData.message || 'Branch not found'
                    };
                }

                return {
                    success: false,
                    data: responseData.data,
                    message: responseData.message || 'Failed to delete branch'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete branch');
        }
    }
}

export const branchApiService = new BranchApiService();